import type { GameCommand } from '../game/types';

export class GameInput {
  private held = new Set<string>();
  private pointers = new Map<number, string>();
  private abort = new AbortController();
  enabled = false;
  constructor(private pause: () => void, private zoom: (delta: number) => void, private fire: (side: 'port' | 'starboard') => void) {
    const signal = this.abort.signal;
    const keys: Record<string, string> = { KeyA: 'left', ArrowLeft: 'left', KeyD: 'right', ArrowRight: 'right', KeyW: 'faster', ArrowUp: 'faster', KeyS: 'slower', ArrowDown: 'slower', KeyQ: 'port', KeyE: 'starboard' };
    window.addEventListener('keydown', (event) => {
      if ((event.target as HTMLElement)?.matches('input,select,textarea')) return;
      const focusedControl = (event.target as HTMLElement)?.closest<HTMLElement>('[data-input]');
      if (this.enabled && focusedControl && (event.code === 'Space' || event.code === 'Enter')) {
        const control = focusedControl.dataset.input!;
        this.held.add(control);
        if (!event.repeat && (control === 'port' || control === 'starboard')) this.fire(control);
        event.preventDefault(); return;
      }
      if (event.code === 'Escape' && !event.repeat) { this.pause(); event.preventDefault(); return; }
      if (!this.enabled) return;
      const action = keys[event.code];
      if (action) {
        this.held.add(action);
        if (!event.repeat && (action === 'port' || action === 'starboard')) this.fire(action);
        event.preventDefault();
      }
    }, { signal });
    window.addEventListener('keyup', (event) => {
      const action = keys[event.code]; if (action) this.held.delete(action);
      if (event.code === 'Space' || event.code === 'Enter') {
        const focused = (event.target as HTMLElement)?.closest<HTMLElement>('[data-input]');
        if (focused) this.held.delete(focused.dataset.input!);
      }
    }, { signal });
    window.addEventListener('blur', () => this.clear(), { signal });
    document.addEventListener('pointerdown', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>('[data-input]');
      if (!button || !this.enabled) return;
      const action = button.dataset.input!;
      this.pointers.set(event.pointerId, action);
      if (action === 'port' || action === 'starboard') this.fire(action);
      button.setPointerCapture(event.pointerId);
      button.classList.add('held'); event.preventDefault();
    }, { signal });
    const release = (event: PointerEvent) => {
      this.pointers.delete(event.pointerId);
      document.querySelectorAll('[data-input].held').forEach((node) => node.classList.remove('held'));
    };
    document.addEventListener('pointerup', release, { signal });
    document.addEventListener('pointercancel', release, { signal });
    document.addEventListener('lostpointercapture', release, { signal });
    document.addEventListener('wheel', (event) => {
      if (!this.enabled || (event.target as HTMLElement).closest('button,input,select,details')) return;
      this.zoom(Math.sign(event.deltaY)); event.preventDefault();
    }, { passive: false, signal });
  }
  poll(throttle: number, dt: number): GameCommand[] {
    if (!this.enabled) return [];
    const held = (name: string) => this.held.has(name) || [...this.pointers.values()].includes(name);
    const commands: GameCommand[] = [{ type: 'steer', value: Number(held('right')) - Number(held('left')) }];
    const change = Number(held('faster')) - Number(held('slower'));
    if (change) commands.push({ type: 'throttle', value: Math.max(0, Math.min(1, throttle + change * dt * 0.65)) });
    if (held('port')) commands.push({ type: 'fire', side: 'port' });
    if (held('starboard')) commands.push({ type: 'fire', side: 'starboard' });
    return commands;
  }
  clear(): void { this.held.clear(); this.pointers.clear(); document.querySelectorAll('.held').forEach((node) => node.classList.remove('held')); }
  dispose(): void { this.abort.abort(); this.clear(); }
}
